import { Injectable, inject } from '@angular/core';
import { AppConfigService } from './app-config.service';
import { Observable, from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

/**
 * Service to provide access to environment configuration
 * This now uses AppConfigService to load from config.json instead of environment files
 */
@Injectable({
	providedIn: 'root',
})
export class EnvironmentService {
	private readonly appConfigService = inject(AppConfigService);

	// Track initialization state
	private _isInitialized = false;
	private _initPromise: Promise<unknown> | null = null;

	constructor() {
		// Start initialization immediately
		void this.initialize();
	}

	/**
	 * Initialize environment service by loading config
	 * This is both called internally and by APP_INITIALIZER
	 */
	public initialize(): Promise<unknown> {
		if (this._initPromise) {
			return this._initPromise;
		}

		// console.log('Initializing EnvironmentService...');
		this._initPromise = this.appConfigService
			.loadConfig()
			.then((config) => {
				// console.log('Environment configuration loaded:', config);
				this._isInitialized = true;
				return config;
			})
			.catch((error) => {
				console.error('Failed to load environment configuration:', error);
				return Promise.reject(error);
			});

		return this._initPromise;
	}

	/**
	 * Wait for environment service to be ready
	 */
	ready(): Observable<boolean> {
		if (this._isInitialized) {
			return of(true);
		}

		return from(this.initialize()).pipe(
			map(() => true),
			catchError((err) => {
				console.error('Environment service initialization failed:', err);
				return of(false);
			}),
		);
	}

	/**
	 * Check if environment service is initialized
	 */
	get isInitialized(): boolean {
		return this._isInitialized;
	}

	/**
	 * Get API URL from configuration
	 */
	get apiUrl(): string {
		return this.appConfigService.getConfig().apiUrl;
	}

	/**
	 * Get Auth Client ID from configuration
	 */
	get authClientId(): string {
		return this.appConfigService.getConfig().authClientId;
	}

	/**
	 * Check if environment is production
	 */
	get isProduction(): boolean {
		return this.appConfigService.getConfig().production;
	}

	/**
	 * Get application version
	 */
	get version(): string {
		return this.appConfigService.getConfig().version;
	}

	/**
	 * Get application PING redirect URL
	 */
	get redirectPingUrl(): string {
		return this.appConfigService.getConfig().redirectPingUrl;
	}

	/**
	 * Get login PING URL (identity provider URL)
	 */
	get loginPingUrl(): string {
		return this.appConfigService.getConfig().loginPingUrl;
	}

	/**
	 * Get second user registration form URL
	 */
	get secondRegistrationForm(): string {
		return this.appConfigService.getConfig().secondRegistrationForm;
	}
}
